<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { FormType } from '@/helpers/Form';
import { Option } from '@/helpers/Interfaces';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';

interface ItemModel {
    id: number;
    position: number;
    name: string;
    logo?: FileResource;
    isUrl: boolean;
    isAppModule: boolean;
    urlPath?: string;
    urlName?: string;
    urlTargetBlank: boolean;
    pageId?: number;
    module?: string;
    selectedType: Types;
}

export interface FormSettings {
    settings: {
        model: ItemModel[];
    };
}

defineOptions({
    name: 'widget-mobile-bottom-menu-settings',
});

const { $t } = useLocalization();

const MAX_ITEMS = 5;

enum Types {
    Page = 'Page',
    Url = 'Url',
    AppModule = 'AppModule',
}

const moduleOptions: Option<string>[] = [
    { value: 'Panel', text: $t('[[[Panel]]]') },
    { value: 'ScanQr', text: $t('[[[Skan QR]]]') },
    { value: 'Sales', text: $t('[[[Sprzedaż]]]') },
    { value: 'Lottery', text: $t('[[[Loteria]]]') },
    { value: 'WowGift', text: $t('[[[Wow Gift]]]') },
];

function moduleLabel(value: string): string
{
    return moduleOptions.find((p) => p.value === value)?.text ?? '';
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    model: [],
};

const selected = ref<number | null>(null);
const editoPageSelectorRef = ref<InstanceType<any>>(null);

const page = computed(() => editoPageSelectorRef.value?.page);
const items = computed(() => model.value.settings?.model ?? []);
const form = computed(() => items.value.find((p) => p.id == selected.value));
const canAdd = computed(() => items.value.length < MAX_ITEMS);

function onAdd(): void
{
    if (!canAdd.value) return;

    const item: ItemModel = {
        id: Date.now(),
        position: items.value.length + 1,
        name: $t('[[[-]]]'),
        logo: null,
        isUrl: false,
        isAppModule: false,
        urlTargetBlank: false,
        pageId: null,
        module: null,
        selectedType: Types.Page,
    };

    items.value.push(item);
    selected.value = item.id;
}

function onDelete(item: ItemModel): void
{
    const index = items.value.findIndex((p) => p.id == item.id);

    if (index < 0) return;

    items.value.splice(index, 1);
    items.value.forEach((p, i) => { p.position = i + 1; });

    if (item.id == selected.value)
    {
        selected.value = null;
    }
}

function onSelect(item: ItemModel): void
{
    selected.value = item.id;
}

function moveLeft(item: ItemModel): void
{
    const index = items.value.findIndex((p) => p.id == item.id);

    if (index <= 0) return;

    [items.value[index - 1], items.value[index]] = [items.value[index], items.value[index - 1]];
    items.value.forEach((p, i) => { p.position = i + 1; });
}

function moveRight(item: ItemModel): void
{
    const index = items.value.findIndex((p) => p.id == item.id);

    if (index < 0 || index >= items.value.length - 1) return;

    [items.value[index], items.value[index + 1]] = [items.value[index + 1], items.value[index]];
    items.value.forEach((p, i) => { p.position = i + 1; });
}

function updateType(value: Types): void
{
    if (value === Types.Page)
    {
        form.value.name = page.value?.name?.trim?.() || $t('[[[-]]]');
    }

    if (value === Types.Url)
    {
        form.value.name = form.value.urlName || $t('[[[-]]]');
    }

    if (value === Types.AppModule)
    {
        if (!form.value.module)
        {
            form.value.module = moduleOptions[0].value;
        }

        form.value.name = moduleLabel(form.value.module);
    }

    form.value.isUrl = value === Types.Url;
    form.value.isAppModule = value === Types.AppModule;
}

watch(
    () => page.value,
    (value) =>
    {
        if (form.value && !form.value.isUrl)
        {
            form.value.name = value?.name?.trim?.() || $t('[[[-]]]');
        }
    },
    { flush: 'post' }
);

watch(
    () => form.value?.urlName,
    (value) =>
    {
        if (form.value && form.value.isUrl)
        {
            form.value.name = value || $t('[[[-]]]');
        }
    },
    { flush: 'post' }
);

watch(
    () => form.value?.module,
    (value) =>
    {
        if (form.value && form.value.isAppModule)
        {
            form.value.name = moduleLabel(value);
        }
    },
    { flush: 'post' }
);

onMounted(async () =>
{
    model.value.settings = { ...defaults, ...model.value.settings };

    const min = items.value.reduce((prev, current) => (prev?.position < current.position ? prev : current), null);

    selected.value = min?.id ?? null;
});
</script>

<template>
    <div>
        <ideo-button icon="fas fa-plus" class="mb-2" :disabled="!canAdd" @click="onAdd">
            {{ $t('[[[Dodaj]]]') }}
        </ideo-button>
        <div v-if="!canAdd" class="text-muted small mb-2">
            {{ $t('[[[Maksymalnie 5 pozycji w dolnym menu]]]') }}
        </div>
        <div v-if="items.length == 0">
            <p class="text-center py-3 mb-0">
                {{ $t('[[[Nie ma jeszcze żadnych rekordów]]]') }}
            </p>
        </div>
        <div v-else class="d-flex flex-row flex-wrap gap-3 mb-3">
            <div
                v-for="item in items" :key="item.id"
                class="border rounded p-3 d-flex flex-column align-items-center"
                :class="{ 'border-primary bg-body-tertiary': item.id == selected }"
                style="width: 140px; cursor: pointer;"
                @click="onSelect(item)"
            >
                <img v-if="item.logo?.url" :src="item.logo.url" class="mb-2" style="width: 48px; height: 48px; object-fit: contain;" />
                <i v-else class="fas fa-image text-muted mb-2" style="font-size: 32px;"></i>
                <div class="text-truncate w-100 text-center">{{ item.name }}</div>
                <div class="d-flex gap-1 mt-2">
                    <ideo-button size="sm" variant="light" icon="fas fa-chevron-left" @click.stop="moveLeft(item)"></ideo-button>
                    <ideo-button size="sm" variant="light" icon="fas fa-trash-alt" @click.stop="onDelete(item)"></ideo-button>
                    <ideo-button size="sm" variant="light" icon="fas fa-chevron-right" @click.stop="moveRight(item)"></ideo-button>
                </div>
            </div>
        </div>
        <div v-if="form">
            <ideo-form-group v-if="!form.isAppModule" :label="$t('[[[Ikonka]]]')">
                <filepicker v-model="form.logo" media="images" />
            </ideo-form-group>
            <ideo-form-radio v-model="form.selectedType" :value="Types.Page" name="selectedType" @update:model-value="updateType">{{ $t('[[[Strona]]]') }}</ideo-form-radio>
            <ideo-form-radio v-model="form.selectedType" :value="Types.Url" name="selectedType" @update:model-value="updateType">{{ $t('[[[Adres URL]]]') }}</ideo-form-radio>
            <ideo-form-radio v-model="form.selectedType" :value="Types.AppModule" name="selectedType" @update:model-value="updateType">{{ $t('[[[Moduł aplikacji]]]') }}</ideo-form-radio>
            <ideo-form-group v-if="!form.isUrl && !form.isAppModule" :label="$t('[[[Wybierz stronę]]]')">
                <edito-page-selector
                    v-model="form.pageId"
                    ref="editoPageSelectorRef"
                    :module="null"
                    label=""
                    :preview="false"
                    :no-preview="true"
                    class="mb-0"
                />
            </ideo-form-group>
            <ideo-form-group v-if="form.isAppModule" :label="$t('[[[Moduł]]]')">
                <ideo-form-select v-model="form.module" :options="moduleOptions" @update:model-value="updateType(Types.AppModule)" />
            </ideo-form-group>
            <ideo-form-group v-if="form.isUrl" :label="$t('[[[Adres]]]')">
                <ideo-form-input v-model="form.urlPath" type="text"></ideo-form-input>
            </ideo-form-group>
            <ideo-form-group v-if="form.isUrl" :label="$t('[[[Nazwa]]]')">
                <ideo-form-input v-model="form.urlName" type="text"></ideo-form-input>
            </ideo-form-group>
            <ideo-form-group v-if="form.isUrl">
                <ideo-form-checkbox v-model="form.urlTargetBlank">
                    {{ $t('[[[Otwórz w przeglądarce wewnętrznej]]]') }}
                </ideo-form-checkbox>
            </ideo-form-group>
        </div>
    </div>
</template>
